/**
 * Performance Optimization Hook
 * Provides automatic performance optimization and monitoring
 */

import { useEffect, useRef, useState } from 'react';
import { useAnimation } from './context';
import { onPerformanceBudgetViolation } from './performance';

interface PerformanceOptimizationOptions {
  autoOptimize?: boolean;
  enableBudgetAlerts?: boolean;
  enableAutoCleanup?: boolean;
  cleanupInterval?: number;
}

interface PerformanceState {
  isOptimized: boolean;
  currentLevel: 'high' | 'medium' | 'low';
  violations: number;
  recommendations: string[];
}

export const usePerformanceOptimization = (
  options: PerformanceOptimizationOptions = {}
) => {
  const {
    autoOptimize = true,
    enableBudgetAlerts = true,
    enableAutoCleanup = true,
    cleanupInterval = 30000
  } = options;

  const { config, updateConfig, performanceMonitor, performanceMetrics } = useAnimation();
  const [performanceState, setPerformanceState] = useState<PerformanceState>({
    isOptimized: false,
    currentLevel: config.performance,
    violations: 0,
    recommendations: []
  });

  const violationCountRef = useRef(0);
  const lastOptimizationRef = useRef(0);
  const cleanupIntervalRef = useRef<NodeJS.Timeout>();

  // Auto-optimization logic
  useEffect(() => {
    if (!autoOptimize) return;

    const checkPerformance = () => {
      const metrics = performanceMetrics;
      const now = Date.now();
      
      // Avoid too frequent optimizations
      if (now - lastOptimizationRef.current < 5000) return;

      let shouldOptimize = false;
      let newLevel = config.performance;

      // Check if we need to reduce performance level
      if (metrics.fps < 45 && config.performance === 'high') {
        newLevel = 'medium';
        shouldOptimize = true;
      } else if (metrics.fps < 30 && config.performance === 'medium') {
        newLevel = 'low';
        shouldOptimize = true;
      }

      // Check if we can increase performance level
      if (metrics.fps > 58 && violationCountRef.current === 0) {
        if (config.performance === 'low') {
          newLevel = 'medium';
          shouldOptimize = true;
        } else if (config.performance === 'medium') {
          newLevel = 'high';
          shouldOptimize = true;
        }
      }

      if (shouldOptimize) {
        console.log(`Auto-optimizing performance level: ${config.performance} → ${newLevel}`);
        
        updateConfig({
          performance: newLevel,
          enableComplexEffects: newLevel !== 'low',
          maxConcurrentAnimations: newLevel === 'high' ? 15 : newLevel === 'medium' ? 8 : 3,
        });

        lastOptimizationRef.current = now;
        setPerformanceState(prev => ({
          ...prev,
          isOptimized: true,
          currentLevel: newLevel
        }));
      }
    };

    const interval = setInterval(checkPerformance, 2000);
    return () => clearInterval(interval);
  }, [autoOptimize, config.performance, performanceMetrics, updateConfig]);

  // Budget violation monitoring
  useEffect(() => {
    if (!enableBudgetAlerts) return;

    const unsubscribe = onPerformanceBudgetViolation((violation, severity) => {
      violationCountRef.current++;
      
      setPerformanceState(prev => ({
        ...prev,
        violations: violationCountRef.current,
        recommendations: performanceMonitor.getRecommendations()
      }));

      // Auto-optimize on high severity violations
      if (autoOptimize && severity === 'high') {
        const now = Date.now();
        if (now - lastOptimizationRef.current > 3000) {
          console.warn('High severity performance violation - auto-optimizing');
          
          let newLevel = config.performance;
          if (config.performance === 'high') newLevel = 'medium';
          else if (config.performance === 'medium') newLevel = 'low';

          if (newLevel !== config.performance) {
            updateConfig({
              performance: newLevel,
              enableComplexEffects: newLevel !== 'low',
              maxConcurrentAnimations: newLevel === 'high' ? 15 : newLevel === 'medium' ? 8 : 3,
            });
            lastOptimizationRef.current = now;
          }
        }
      }
    });

    return unsubscribe;
  }, [enableBudgetAlerts, autoOptimize, config.performance, performanceMonitor, updateConfig]);

  // Auto-cleanup
  useEffect(() => {
    if (!enableAutoCleanup) return;

    cleanupIntervalRef.current = setInterval(() => {
      performanceMonitor.forceCleanup();
    }, cleanupInterval);

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, [enableAutoCleanup, cleanupInterval, performanceMonitor]);

  // Manual optimization controls
  const optimizePerformance = (level: 'high' | 'medium' | 'low') => {
    updateConfig({
      performance: level,
      enableComplexEffects: level !== 'low',
      maxConcurrentAnimations: level === 'high' ? 15 : level === 'medium' ? 8 : 3,
    });

    setPerformanceState(prev => ({
      ...prev,
      currentLevel: level,
      isOptimized: true
    }));
  };

  const forceCleanup = () => {
    performanceMonitor.forceCleanup();
  };

  const resetViolations = () => {
    violationCountRef.current = 0;
    setPerformanceState(prev => ({
      ...prev,
      violations: 0
    }));
  };

  return {
    performanceState,
    optimizePerformance,
    forceCleanup,
    resetViolations,
    metrics: performanceMetrics,
  };
};

export default usePerformanceOptimization;